<template>
	<div>
		<v-card v-if="getEditCommentLength() == 0" flat :elevation="0">
			<at-ta
				name-key="name"
				:members="localUsers"
				:ats="['@']"
				suffix=" "
				hide-on-blur
				v-click-outside="closeMention"
				class="mention-dropdown"
				:class="{
					'mention-dropdown-mobile': this.$vuetify.breakpoint.smAndDown || this.lightBoxView,
					'mention-dropdown-open-bottom': this.openMentionDropdownBottom,
				}"
				ref="mentionDropdown"
				@at="adjustMentionDropdownDimensions">
				<div>
					<v-textarea
						v-model="comment"
						@input="onInput"
						@focus="commentFieldFocused = true"
						@blur="commentFieldFocused = false"
						no-resize
						ref="commentField"
						:placeholder="placeholder"
						rounded
						outlined
						:counter="255"
						:maxLength="255"
						@keydown="editNotesKeyDown"
						@keydown.ctrl.enter="addComment"
						@keydown.meta.enter="addComment"
						@click.native="closeMention"
						class="custom-text-field"
						:hide-details="comment.length < 225"
						auto-grow
						:max-rows="4"
						:rows="1"
						:class="{
							'custom-text-field-overflow': comment.trim().length > 0,
							'hide-scrollbars': isDesktop,
						}"
						v-test="'comments-input'">
						<template #append>
							<v-icon
								v-test="'comments-send-button'"
								@click="addComment()"
								:disabled="comment.trim().length === 0"
								:color="sendButtonColor">
								icon-paper-plane-stroke
							</v-icon>
						</template>
					</v-textarea>
				</div>
			</at-ta>
		</v-card>
		<div v-else class="page d-flex justify-space-between">
			<v-avatar
				:color="getSeededAvatarColor(setStartedByColum(editComment))"
				class="team-avatar"
				:style="{ 'background-color': getUserAvatarBackgroundColor(editComment) }">
				{{ getAgentNameInitials(setStartedByColum(editComment)) }}
			</v-avatar>
			<div class="rounded-border">
				<div class="d-flex align-center px-2 pt-2">
					<span class="user-name text-truncate">{{ setStartedByColum(editComment) }}</span>
					<div class="date">{{ getDisplayDate(editComment) }}</div>
				</div>
				<at-ta
					name-key="name"
					:members="localUsers"
					:ats="['@']"
					suffix=" "
					hide-on-blur
					v-click-outside="closeMention"
					class="mention-dropdown"
					:class="{
						'mention-dropdown-mobile': this.$vuetify.breakpoint.smAndDown || this.lightBoxView,
						'mention-dropdown-open-bottom': this.openMentionDropdownBottom,
					}"
					ref="mentionDropdown"
					@at="adjustMentionDropdownDimensions">
					<div class="textarea-wrapper">
						<v-textarea
							v-model="comment"
							@input="onInput"
							ref="commentField"
							no-resize
							:outlined="false"
							:placeholder="placeholder"
							:counter="255"
							:maxLength="255"
							@keydown="editNotesKeyDown"
							@click:append="addComment"
							class="custom-text-field"
							:hide-details="comment.length < 225"
							:max-rows="4"
							:rows="1"
							solo
							flat
							auto-grow
							:class="{
								'custom-text-field-overflow': comment.trim().length > 0,
								'hide-scrollbars': isDesktop,
							}">
						</v-textarea>
					</div>
				</at-ta>
				<div class="pa-2 d-flex justify-end">
					<v-btn rounded class="white-bg" elevation="0" @click="closeEdit" color="grey" text>Cancel</v-btn>
					<v-btn
						rounded
						:disabled="
							comment == sanitiseComment(editComment.content) ||
							comment.trim().length == 0 ||
							comment.length > 255
						"
						@click="editCommentText"
						color="primary"
						elevation="0"
						>Save</v-btn
					>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { getSeededAvatarColor } from "../helpers/utilities";
	import EventBus from "@/helpers/eventBus";
	import {
		getUserAvatarBackgroundColor,
		setStartedByColumn,
		getAgentNameInitials,
		getDisplayDate,
	} from "../helpers/utilities";
	import DeviceUtility from "../helpers/device";
	import AtTa from "vue-at/dist/vue-at-textarea";
	import clickOutside from "../directives/clickOutside";

	export default {
		components: {
			AtTa,
		},
		directives: {
			clickOutside,
		},
		data() {
			return {
				comment: "",
				localUsers: [],
				filteredUsers: [],
				showMentions: false,
				currentUserSelection: null,
				commentPlaceholders: {},
				highlightedParts: [],
				isLandscape: window.matchMedia("(orientation: landscape)").matches,
				commentFieldFocused: false,
				openMentionDropdownBottom: true,
			};
		},
		props: {
			projectId: {
				type: String,
				default: null,
			},
			isReply: {
				type: Boolean,
				default: false,
			},
			placeholder: {
				type: String,
				default: "Type to leave a comment...",
			},
			editComment: {
				type: Object,
				default: () => ({}),
			},
			lightBoxView: {
				type: Boolean,
				default: false,
			},
			projectShareUsers: {
				type: Array,
				default: () => [],
			},
			users: {
				type: Array,
				default: () => [],
				required: true,
			},
		},
		watch: {
			projectShareUsers: {
				handler(newVal) {
					this.setLocalUsers(newVal);
				},
				deep: true,
			},
		},
		async mounted() {
			this.localUsers = this.users.map((user) => ({ name: user.name, id: user.id, value: user.id }));
			this.setLocalUsers(this.projectShareUsers);
			if (this.editComment?.content?.length > 0) {
				this.comment = this.editComment.content;
				if (this.editComment.mentionedUsers) {
					this.setLocalUsers(this.editComment.mentionedUsers);
				}
				this.onInput();
			}
			EventBus.$on("UserRemovedFromProject", (projectShare, projectId) => {
				if (projectId === this.projectId) {
					const id = projectShare.User?.id;
					if (id) {
						this.localUsers = this.localUsers?.filter((u) => u.id !== id);
					}
				}
			});
			window.addEventListener("resize", this.adjustMentionDropdownWidth);
			window.visualViewport?.addEventListener("resize", this.adjustMentionDropdownPosition);
		},
		beforeDestroy() {
			window.removeEventListener("resize", this.adjustMentionDropdownWidth);
			window.visualViewport?.removeEventListener("resize", this.adjustMentionDropdownPosition);
		},
		methods: {
			closeMention() {
				this.$refs.mentionDropdown.closePanel();
			},
			adjustMentionDropdownDimensions() {
				this.adjustMentionDropdownWidth();
				this.adjustMentionDropdownPosition();
			},
			adjustMentionDropdownWidth() {
				this.$nextTick(() => {
					const textareaEl = this.$refs.commentField.$el.querySelector("textarea");
					const dropdownEl = this.$refs.mentionDropdown.$el.querySelector(".atwho-view");
					if (!textareaEl || !dropdownEl) return;

					const width = textareaEl.getBoundingClientRect().width;
					dropdownEl.style.width = `${width}px`;
					dropdownEl.style.maxWidth = `${width}px`;
					if (this.$vuetify.breakpoint.mdAndUp) {
						dropdownEl.style.maxWidth = `500px`;
					}
				});
			},
			adjustMentionDropdownPosition() {
				this.$nextTick(() => {
					const textareaEl = this.$refs.commentField.$el.querySelector("textarea");
					const dropdownEl = this.$refs.mentionDropdown.$el.querySelector(".atwho-view");
					if (!textareaEl || !dropdownEl) return;

					//place the dropdown based on the available space
					const rect = textareaEl.getBoundingClientRect();
					const dropdownHeight = dropdownEl.offsetHeight;
					const availableSpaceBelow = window.innerHeight - rect.bottom;
					if (availableSpaceBelow >= dropdownHeight) {
						this.openMentionDropdownBottom = true;
					} else {
						this.openMentionDropdownBottom = false;
					}
				});
			},
			setLocalUsers(newVal) {
				const projectShareUsers = newVal.filter((user) => !!user);
				const projectShareUniqueUsers = projectShareUsers.filter(
					(u) => !this.localUsers.some((user) => user.id === u.id)
				);
				this.localUsers = [
					...this.localUsers,
					...projectShareUniqueUsers.map((user) => ({
						name: user.name ? user.name : `${user.firstname} ${user.lastname}`,
						id: user.id,
						value: user.id,
					})),
				];
			},
			ellipsifyUserName(name) {
				if (name.length > 25) {
					return name.substring(0, 22) + "...";
				}
				return name;
			},
			getSeededAvatarColor(name) {
				return getSeededAvatarColor(name);
			},
			editNotesKeyDown(e) {
				if (["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"].includes(e.code)) {
					e.stopPropagation();
				}
			},
			getEditCommentLength() {
				return this.editComment?.content?.length || 0;
			},
			getUserAvatarBackgroundColor(comment) {
				return getUserAvatarBackgroundColor(comment);
			},
			setStartedByColum(item) {
				return setStartedByColumn(item, this.me?.user.id);
			},
			getAgentNameInitials(agentName) {
				return getAgentNameInitials(agentName);
			},
			getDisplayDate(comment) {
				return getDisplayDate(comment);
			},
			sanitiseComment(comment) {
				if (comment.includes("@")) {
					for (const user of this.localUsers) {
						if (comment.includes(`@${user.id}`)) {
							comment = comment.replaceAll(`@${user.id}`, `@${user.name}`); // we replace the id with the name while rendering the comment.
							this.commentPlaceholders[user.name] = user.id;
						}
					}
				}
				return comment;
			},
			onInput() {
				let comment = this.comment;
				if (comment.includes("@")) {
					for (const user of this.localUsers) {
						if (comment.includes(`@${user.id}`)) {
							comment = comment.replaceAll(`@${user.id}`, `@${user.name}`); // we replace the id with the name while rendering the comment.
							this.commentPlaceholders[user.name] = user.id;
						} else if (comment.includes(`@${user.name}`)) {
							this.commentPlaceholders[user.name] = user.id;
						}
					}
				}
				this.$nextTick(() => {
					if (!this.$refs.commentField) return;
					if (!this.$refs.commentField.style) {
						this.$refs.commentField.style = {};
					}
					this.$refs.commentField.style.height = "auto";
					this.$refs.commentField.style.height = `${this.$refs.commentField?.scrollHeight}px`;
				});
				this.comment = comment;
			},
			addComment() {
				this.updateComment(false);
			},
			editCommentText() {
				this.updateComment(true);
			},
			closeEdit() {
				this.$emit("closeEdit");
			},
			clearComment() {
				this.comment = "";
				this.commentPlaceholders = {};
			},
			updateComment(isEditing) {
				let comment = this.comment.trim();
				if (comment.trim().length === 0 || comment.length > 255) {
					return;
				}
				for (const [name, id] of Object.entries(this.commentPlaceholders)) {
					comment = comment.replaceAll(`@${name}`, `@${id}`); // while saving the comment, we replace the name with the id.
				}
				if (isEditing) {
					this.$emit("update:editComment", comment);
				} else {
					this.$emit("update:addComment", comment);
					this.comment = "";
				}
			},
			cancelReply() {
				this.$emit("cancelReply");
			},
		},
		computed: {
			isDesktop() {
				return DeviceUtility.isDesktop;
			},
			sendButtonColor() {
				return this.comment.trim().length > 0 && this.commentFieldFocused ? "primary" : "grey";
			},
		},
	};
</script>

<style lang="css" scoped>
	.custom-text-field-overflow >>> textarea {
		overflow-y: auto !important;
	}
	.hide-scrollbars >>> textarea {
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none;
	}

	.hide-scrollbars >>> textarea::-webkit-scrollbar {
		display: none;
		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none;
	}

	.date {
		font-size: 0.8rem;
		color: #999;
		margin-left: 10px;
	}
	.team-avatar {
		display: flex;
		justify-content: center;
		font-size: 0.75em;
		font-weight: 600;
		color: #fff;
		margin-right: 10px;
		min-width: 40px !important;
		max-width: 40px !important;
		max-height: 40px !important;
		text-transform: uppercase;
	}

	.v-card.page {
		padding: 0px !important;
		width: 95% !important;
		margin-left: 2.5%;
	}

	.custom-text-field >>> .v-input__control > .v-input__slot {
		padding: 0 12px !important;
	}

	.replies-line {
		display: flex;
		align-items: center;
		color: black;
		opacity: 0.6;
	}

	.replies-line .line {
		flex-grow: 1;
		height: 1px;
		background-color: black;
		opacity: 0.6;
		margin-left: 10px;
	}

	:deep(.custom-text-field .v-input__slot textarea) {
		min-height: 0 !important;
		max-height: 100px !important;
	}

	.rounded-border {
		border: 1px solid black;
		border-radius: 12px;
		width: 100%;
	}

	.v-input.v-input--hide-details {
		margin-bottom: 0px !important;
	}

	/* custom css for at-ta component to match other fields in the app */

	.mention-dropdown ::v-deep .atwho-view {
		padding: 8px 0px !important;
		background-color: white !important;
		border-radius: 0 !important;
	}

	.mention-dropdown-open-bottom ::v-deep .atwho-view {
		top: 40px !important;
		bottom: auto !important;
	}

	.mention-dropdown ::v-deep .atwho-ul {
		padding-left: 0px !important;
		max-height: 250px !important;
	}

	.mention-dropdown ::v-deep .atwho-ul li {
		min-height: 48px !important;
		align-items: center;
		display: flex;
		padding: 0px 16px !important;
	}

	.mention-dropdown ::v-deep .atwho-ul li span {
		padding: 12px 0px !important;
	}

	.mention-dropdown ::v-deep .atwho-ul li:hover {
		cursor: pointer;
	}

	.mention-dropdown ::v-deep .atwho-cur {
		background: #2d74ff !important;
	}

	.custom-text-field ::v-deep .v-text-field__slot {
		margin-top: 4px !important;
	}

	.mention-dropdown-mobile ::v-deep .atwho-panel {
		left: 16px !important;
	}

	.user-name {
		font-size: 0.8rem;
		color: #03011d99;
		max-width: 150px !important;
	}
</style>
